<script setup lang="ts">
  import { ref, onMounted, watch } from 'vue';
  import { useModal } from '@/composition/use-modal';
  import { useCoupons } from '@/composition/use-coupons';
  import { ICoupon } from '@/interfaces/ICoupon';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { IOrder } from '@/interfaces/IOrder';
  import { ICustomer } from '@/interfaces/ICustomer';
  import { IUser } from '@/interfaces/IUser';
  import { useOrders } from '@/composition/use-orders';
  import { IDataOptions } from '@/interfaces/IDataOptions';
  import { debounce } from '@/utils';

  interface Props {
    coupon: ICoupon;
  }

  interface ICouponUse {
    id: number;
    couponId: number;
    orderId: number;
    customerId: number | null;
    userId: number | null;
    discountAmount: number;
    usedAt: Date | string;
    customer?: ICustomer;
    user?: IUser;
    order?: IOrder;
    coupon?: ICoupon;
  }

  const props = defineProps<Props>();
  const modals = useModal();
  const couponsHelper = useCoupons();
  const restaurantStore = useRestaurantStore();
  const ordersHelper = useOrders();

  const loading = ref(false);
  const usageHistory = ref<ICouponUse[]>([]);
  const totalItems = ref(0);

  const pagination = ref<IDataOptions>({
    page: 1,
    itemsPerPage: 10,
    mustSort: true,
    sortBy: ['usedAt'],
    sortDesc: [true],
    search: '',
  });

  const headers = [
    { text: 'Fecha', value: 'usedAt' },
    { text: 'Pedido', value: 'orderId' },
    { text: 'Cliente', value: 'customer' },
    { text: 'Descuento', value: 'discountAmount' },
  ];

  async function loadHistory() {
    loading.value = true;
    try {
      const response = await couponsHelper.getCouponUsageHistory(
        props.coupon.id,
        pagination.value
      );
      usageHistory.value = response.items;
      totalItems.value = response.total;
    } catch (error) {
      // Error already handled by composable
    } finally {
      loading.value = false;
    }
  }

  const debouncedLoadHistory = debounce(loadHistory, 500);

  watch(
    pagination,
    (newVal, oldVal) => {
      if (newVal.search !== oldVal.search) {
        debouncedLoadHistory();
      } else {
        loadHistory();
      }
    },
    { deep: true }
  );

  onMounted(() => {
    loadHistory();
  });

  function close() {
    modals.hide('v2-coupon-usage');
  }

  function formatDate(date: Date | string): string {
    return new Date(date).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function formatCurrency(amount: number): string {
    if (!Number.isFinite(amount)) amount = 0;
    return `${restaurantStore.currency.symbol}${amount.toFixed(2)}`;
  }

  function getCustomerName(use: ICouponUse): string {
    if (use.customer) {
      return (
        use.customer.name || use.customer.email || `Cliente #${use.customerId}`
      );
    }
    if (use.user) {
      return use.user.name || use.user.email || `Usuario #${use.userId}`;
    }
    return 'Anónimo';
  }

  function viewOrder(orderId: number) {
    ordersHelper.openOrder(orderId, false);
  }

  function getDiscountedAmount(use: ICouponUse): number {
    if (!use.order) {
      console.warn('Order not found');
      return 0;
    }

    const orderSubtotal = use.order.subtotal;
    const couponValue = props.coupon.value;

    if (props.coupon.isPercentage) {
      return (orderSubtotal * couponValue) / 100;
    }
    return Math.min(couponValue, orderSubtotal);
  }
</script>

<template>
  <div class="v2-modal-coupon-usage">
    <div class="modal-header">
      <h3 class="modal-title">Historial de uso - {{ coupon.code }}</h3>
    </div>

    <div class="modal-body">
      <!-- Coupon summary -->
      <v-card outlined class="mb-4">
        <v-card-text>
          <div class="usage-summary">
            <div class="summary-item">
              <div class="summary-label">Cupón</div>
              <div class="summary-value">{{ coupon.name }}</div>
            </div>

            <div class="summary-item">
              <div class="summary-label">Valor</div>
              <div class="summary-value">
                {{ couponsHelper.formatCouponValue(coupon) }}
              </div>
            </div>

            <div class="summary-item">
              <div class="summary-label">Usos</div>
              <div class="summary-value">
                {{ coupon.currentUses }}
                <span v-if="coupon.maxUses">/ {{ coupon.maxUses }}</span>
              </div>
            </div>

            <div v-if="coupon.maxUsesPerCustomer" class="summary-item">
              <div class="summary-label">Por cliente</div>
              <div class="summary-value">
                Máx. {{ coupon.maxUsesPerCustomer }} usos
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Search field -->
      <v-text-field
        v-model="pagination.search"
        prepend-inner-icon="mdi-magnify"
        label="Buscar"
        placeholder="Cliente, usuario o número de pedido"
        clearable
        hide-details
        outlined
        dense
        class="mb-4"
      />

      <!-- Usage history table -->
      <v-data-table
        :headers="headers"
        :items="usageHistory"
        :loading="loading"
        :options.sync="pagination"
        :server-items-length="totalItems"
        :footer-props="{
          'items-per-page-options': [10, 20, 50],
        }"
        class="usage-table"
      >
        <template v-slot:item.usedAt="{ item }">
          <span class="text-no-wrap">
            {{ formatDate(item.usedAt) }}
          </span>
        </template>

        <template v-slot:item.orderId="{ item }">
          <v-btn text x-small color="primary" @click="viewOrder(item.orderId)">
            #{{ item.orderId }}
          </v-btn>
        </template>

        <template v-slot:item.customer="{ item }">
          {{ getCustomerName(item) }}
        </template>

        <template v-slot:item.discountAmount="{ item }">
          <span class="font-weight-medium">
            {{ formatCurrency(getDiscountedAmount(item)) }}
          </span>
        </template>

        <template v-slot:no-data>
          <div class="text-center py-4 text--secondary">
            <template v-if="pagination.search">
              No se encontraron resultados para "{{ pagination.search }}"
            </template>
            <template v-else> Este cupón aún no ha sido utilizado </template>
          </div>
        </template>
      </v-data-table>

      <!-- Total summary -->
      <v-card v-if="usageHistory.length > 0" outlined class="mt-4">
        <v-card-text>
          <div class="total-summary">
            <div class="total-label">Total descontado en esta página</div>
            <div class="total-value">
              {{
                formatCurrency(
                  usageHistory.reduce(
                    (sum, use) => sum + getDiscountedAmount(use),
                    0
                  )
                )
              }}
            </div>
          </div>
          <div
            v-if="totalItems > pagination.itemsPerPage"
            class="text-caption text--secondary mt-2"
          >
            Mostrando {{ usageHistory.length }} de {{ totalItems }} usos
          </div>
        </v-card-text>
      </v-card>
    </div>

    <div class="modal-footer">
      <v-btn text @click="close"> Cerrar </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-modal-coupon-usage {
    .modal-header {
      @apply tw-px-6 tw-pt-6 tw-pb-4;

      .modal-title {
        @apply tw-text-xl tw-font-medium;
      }
    }

    .modal-body {
      @apply tw-px-6 tw-pb-4;

      .usage-summary {
        @apply tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-4;

        .summary-item {
          .summary-label {
            @apply tw-text-sm tw-text-gray-600 tw-mb-1;
          }

          .summary-value {
            @apply tw-font-medium;
          }
        }
      }

      .usage-table {
        @apply tw-mt-4;
      }

      .total-summary {
        @apply tw-flex tw-justify-between tw-items-center;

        .total-label {
          @apply tw-text-lg tw-font-medium;
        }

        .total-value {
          @apply tw-text-xl tw-font-bold tw-text-green-600;
        }
      }
    }

    .modal-footer {
      @apply tw-px-6 tw-pb-6 tw-pt-2 tw-flex tw-justify-end;
    }
  }
</style>
